<script lang="ts" setup>
import type { IProps } from './UiPolicy.types'
import { useSlot } from '@/composables/useSlot'

const { hasSlot } = useSlot()

withDefaults(defineProps<IProps>(), {
  version: 'primary',
  tableOfContents: false,
})
</script>

<template>
  <section class="policy">
    <header :class="['policy__header', `policy__header--${version}`]">
      <h1 :class="['policy__header-title', `policy__header-title--${version}`]">
        {{ title }}
      </h1>

      <p :class="['policy__header-subtitle', `policy__header-subtitle--${version}`]">
        {{ subtitle }}
      </p>
    </header>
    <div class="policy__layout">
      <div
        v-if="tableOfContents && policy.list"
        class="policy__navigation"
      >
        <h2
          v-if="policy.header"
          class="policy__navigation-header"
        >
          {{ policy.header }}
        </h2>

        <p
          v-if="policy.subheader"
          class="policy__navigation-subheader"
        >
          {{ policy.subheader }}
        </p>

        <ul class="policy__navigation-list">
          <li
            v-for="item in policy.list"
            :key="item.link"
            :class="[
              'policy__navigation-item',
              item.heading && 'policy__navigation-item--show'
            ]"
          >
            <NuxtLink
              :to="`#${item.link}`"
              :title="item.heading"
              class="policy__navigation-link"
            >
              {{ item.heading }}
            </NuxtLink>
          </li>
        </ul>
      </div>

      <div
        v-if="policy.list"
        class="policy__content"
      >
        <div
          v-for="(item, index) in policy.list"
          :key="item.link"
          class="policy__content-block"
        >
          <template v-if="item.onlySlot">
            <template v-if="hasSlot(`before-heading-${index}`)">
              <slot
                :name="`before-heading-${index}`"
                :item="{ ...item }"
              />
            </template>
          </template>

          <template v-else>
            <div :id="item.link">
              <div class="policy__content-block-header-container">
                <p
                  v-if="version === 'secondary'"
                  class="policy__content-block-number"
                >
                  {{ index + 1 }}
                </p>

                <h3
                  v-if="item.heading"
                  :class="['policy__content-block-heading', `policy__content-block-heading--${version}`]"
                >
                  {{ item.heading }}
                </h3>
              </div>

              <div
                :class="['policy__content-block-text', `policy__content-block-text--${version}`]"
                v-html="item.content"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.policy {
  padding: 8.8rem 2.4rem;
  background-color: $gray-100;

  &__header {
    margin-top: 4rem;
    margin-bottom: 4.8rem;
    text-align: center;
  }

  &__header-title {
    margin-bottom: 1.6rem;
    @include h7;
  }

  &__header-subtitle {
    @include body-2-medium;
    margin: 0 auto;
    color: $text-200;

    &--primary {
      max-width: 21rem;
    }

    &--secondary {
      max-width: 60rem;
    }

    @include atM {
      max-width: 75%;
      margin-right: auto;
      margin-left: auto;
    }
  }

  &__layout {
    max-width: 115.2rem;
    margin: 0 auto;

    @include atM {
      display: flex;
      gap: 6.4rem;
    }

    @include atXL {
      gap: 11.5rem;
      max-width: 134rem;
    }
  }

  &__navigation {
    margin-bottom: 4rem;

    @include atM {
      min-width: 34rem;
      width: 34rem;
    }
  }

  &__navigation-header {
    @include h8;
    display: inline-block;
    margin-bottom: 1.6rem;
    border-bottom: 0.16rem solid $purple-400;
  }

  &__navigation-subheader {
    @include body-3-medium;
    color: $text-300;
  }

  &__navigation-list {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin-top: 2.4rem;
  }

  &__navigation-item {
    display: none;

    &--show {
      display: block;
    }
  }

  &__navigation-link {
    @include body-2-medium;
    display: block;
    width: 100%;
    padding: 1.6rem 1.4rem 1.6rem 2.4rem;
    background-color: $white;
    border-radius: 1.6rem;
  }

  &__content-block {
    margin-bottom: 3.2rem;

    @include atM {
      margin-bottom: 4rem;
    }
  }

  &__content-block-header-container {
    display: flex;
    gap: 1.6rem;
  }

  &__content-block-number {
    @include body-1-medium;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 4.2rem;
    width: 4.2rem;
    height: 4.2rem;
    margin-right: 0.8rem;
    color: $text-400;
    letter-spacing: -0.01em;
    background-color: $white;
    border-radius: 50%;
    content: counter(item);
  }

  &__content-block-heading {
    @include h9;
    margin-bottom: 3.2rem;

    @include atM {
      @include h8;
      margin-bottom: 2.8rem;
    }
  }

  &__content-block-text {
    @include body-1;
    overflow-wrap: break-word;

    @include atM {
      @include h10;
      line-height: 3rem;
    }

    :deep() {
      p {
        margin-bottom: 1.6rem;
      }

      b {
        font-weight: 600;
      }

      a {
        color: $purple-400;
      }

      ol {
        margin-top: 1.6rem;
        counter-reset: item;
      }

      ol>li {
        @include body-2-medium;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: 2.6rem;
        overflow-wrap: break-word;
        counter-increment: item;

        @include atM {
          @include h10;
        }

        &:before {

          @include atM {
            min-width: 4.8rem;
            width: 4.8rem;
            height: 4.8rem;
          }
        }
      }

      ul {
        margin-top: 1.6rem;
        padding-left: 3.2rem;
        list-style: disc;
      }

      ul>li {
        @include body-2-medium;

        @include atM {
          @include h10;
        }
      }

      table {
        font-weight: 400;
        border: 1px solid $gray-300;

        tr {
          &:nth-of-type(odd) {
            background-color: $white;
          }

          &:nth-of-type(even) {
            background-color: $gray-100;
          }

          th,
          td {
            @include body-3;
            padding: 1.6rem;
            color: $text-400;
            border: 1px solid $gray-300;

            @include atM {
              @include body-2;
            }

            &:first-of-type {
              font-weight: 600;
            }
          }

          th {
            font-weight: 600;
          }
        }
      }
    }

    &--primary {
      :deep() {
        ol>li {
          position: relative;
          padding-left: 5.8rem;

          &:before {
            position: absolute;
            top: -1rem;
            left: 0;
            @include body-1;
            display: flex;
            justify-content: center;
            align-items: center;
            min-width: 4.2rem;
            width: 4.2rem;
            height: 4.2rem;
            margin-right: 0.8rem;
            color: $text-400;
            letter-spacing: -0.01em;
            background-color: $white;
            border-radius: 50%;
            content: counter(item);
          }
        }
      }
    }

    &--secondary {
      @include body-1;

      :deep() {
        u {
          text-decoration: underline;
        }

        ol>li {
          position: relative;
          display: inline-block;
          padding-left: 4.8rem;

          &:before {
            position: absolute;
            top: 0;
            left: 0.4rem;
            width: 3.2rem;
            height: 3.2rem;
            background-image: url('/icons/lipoint.png');
            background-repeat: no-repeat;
            background-position: center;
            background-size: 100%;
            border-radius: 0.8rem;
            content: '';
          }
        }
      }
    }

    &--document {
      :deep(){
        ul > li::marker {
          color: $purple-400;
        }
      }
    }
  }
}

@include atM {
  .policy {
    &__header {
      margin: 6.4rem auto 8rem auto;
      &--primary {
        max-width: 74rem;
      }

      &--secondary {
        max-width: 132rem;
      }
    }

    &__header-title {
      line-height: 7.2rem;

      &--primary {
        @include h3;
      }

      &--secondary {
        @include h5;
      }

      &--document {
        @include h5;

        @include atM {
          max-width: 75%;
          margin-right: auto;
          margin-left: auto;
        }
      }
    }

    &__content-block-text {
      &--secondary {
        :deep() {
          ol>li {
            &:before {
              left: -1.6rem;
            }
          }
        }
      }
    }
  }
}
</style>
